import { Stack } from '@mui/material';
import { SchemaModel } from '../../../models/schemaModel';
import SingleSelect from '../../../shared/components/SingleSelect';
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import RadioButtons from '../../../shared/components/RadioButtons';
import {
  ValidationResult
, zodNativeValidatorResolver
} from '../../../shared/forms/validation/resolvers/zodNativeValidatorResolver';
import { CreateSchemaDataForm, CreatingSchemaMethod, schemaFormValidation } from '../validation/schemaFormValidation';

type Props = {
  readonly schemas: SchemaModel[];
  readonly onAddSchema: (schemaName: string) => void;
  readonly onCloneSchema: (sourceSchemaName: string, schemaName: string) => void;
};

const INITIAL_DATA_FROM: CreateSchemaDataForm = {
  schemaName: '',
  creatingMethod: CreatingSchemaMethod.EMPTY,
};

const CREATING_SCHEMA_METHOD_OPTIONS = [
  { id: CreatingSchemaMethod.EMPTY, label: 'Create empty schema' },
  { id: CreatingSchemaMethod.CLONE, label: 'Clone configuration from existing schema' },
];

const SchemaCreateForm = (props: Props) => {
  const {
    schemas,
    onAddSchema,
    onCloneSchema,
  } = props;

  const [ dataForm, setDataForm ] = useState<CreateSchemaDataForm>(INITIAL_DATA_FROM);
  const [ validationResult, setValidationResult ] = useState<ValidationResult>({ isValid: false });
  const isCloneDisabled = dataForm.creatingMethod === CreatingSchemaMethod.EMPTY;

  const validateDataForm = (dataForm: CreateSchemaDataForm): ValidationResult => (
    zodNativeValidatorResolver(schemaFormValidation)(dataForm)
  );

  const updateDataForm = (updater: (dataForm: CreateSchemaDataForm) => CreateSchemaDataForm) => {
    setDataForm(dataForm => {
      const newDataForm = updater(dataForm);
      setValidationResult(validateDataForm(newDataForm));

      return newDataForm;
    });
  };

  const handleChangeSchemaName = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDataForm(dataForm => ({
      ...dataForm,
      schemaName: event.target.value,
    }));
  };

  const handleChangeMethod = (method: CreatingSchemaMethod) => {
    updateDataForm(dataForm => ({
      ...dataForm,
      creatingMethod: method,
    }));
  };

  const handleSelectSourceSchema = (sourceSchema: SchemaModel) => {
    updateDataForm(dataForm => ({
      ...dataForm,
      sourceSchemaName: sourceSchema.name,
    }));
  };

  const handleAddClick = () => {
    const { schemaName, creatingMethod, sourceSchemaName } = dataForm;

    if (creatingMethod === CreatingSchemaMethod.EMPTY) {
      onAddSchema(schemaName);
    } else if (creatingMethod === CreatingSchemaMethod.CLONE && sourceSchemaName) {
      onCloneSchema(sourceSchemaName, schemaName);
    }

    setDataForm(INITIAL_DATA_FROM);
  };

  return (
    <Stack spacing={4}>
      <Typography variant='h5'>Create new schema</Typography>

      <TextField
        variant='standard'
        label='Name'
        value={dataForm.schemaName}
        onChange={handleChangeSchemaName}
        required
      />

      <RadioButtons
        label='Method'
        options={CREATING_SCHEMA_METHOD_OPTIONS}
        defaultOptionId={dataForm.creatingMethod}
        onChange={opt => handleChangeMethod(opt.id)}
      />

      <SingleSelect
        label='Clone from'
        options={schemas}
        selectOptionId={schema => schema.name}
        selectOptionLabel={schema => schema.name}
        onSelect={handleSelectSourceSchema}
        disabled={isCloneDisabled}
        required
      />

      <Stack direction='row' spacing={2}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleAddClick}
          disabled={!validationResult.isValid}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};

export default SchemaCreateForm;
